import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SECURITY: send httpOnly session cookie with every request so XSS cannot steal the JWT.
// Bearer token is still attached by AuthContext (backward-compatible for Android/Capacitor).
axios.defaults.withCredentials = true;

// ─── Phase 43 — Global error safety net ─────────────────────────────
// Any API call that fails is intercepted here so:
//   1. `error.friendlyMessage` is populated (blob or json response body
//      → readable string) — components just show it in a toast.
//   2. Unexpected 5xx errors are also POST-ed to /api/errors/client so
//      admin's System Errors page sees a unified feed of user-impact
//      incidents (client-side crashes + server-side exceptions).
// Business-logic 4xx (403 / 404 / 409 / 422) is treated as expected —
// we surface the detail but don't spam the error inbox.
// ────────────────────────────────────────────────────────────────────
async function _extractDetail(err) {
    try {
        const data = err?.response?.data;
        if (data && typeof data === 'object' && !(data instanceof Blob)) {
            // Phase 47 — Detail may itself be a structured object (e.g. our
            // `service_not_authorized_error()` returns
            // {code, service, label_hi, label_en, message_hi, message_en}
            // or Pydantic returns [{loc, msg, type}]). Coerce ALL shapes
            // into a plain string so `toast.error(...)` never receives an
            // object (which triggers React error #31).
            const d = data.detail !== undefined ? data.detail : data;
            return _coerceToString(d) || 'Something went wrong';
        }
        if (data instanceof Blob) {
            const txt = await data.text();
            try {
                const j = JSON.parse(txt);
                return _coerceToString(j.detail !== undefined ? j.detail : j) || txt;
            } catch { return txt; }
        }
        if (typeof data === 'string') return data;
        return err?.message || 'Network error';
    } catch { return err?.message || 'Network error'; }
}

function _coerceToString(x) {
    if (x === null || x === undefined) return '';
    if (typeof x === 'string') return x;
    if (typeof x === 'number' || typeof x === 'boolean') return String(x);
    if (Array.isArray(x)) {
        // Pydantic style [{loc, msg, type}] → join msgs
        return x.map(item => _coerceToString(item)).filter(Boolean).join(' • ');
    }
    if (typeof x === 'object') {
        // Prefer human-readable message keys, in order of preference.
        return (
            x.message_hi
            || x.message_en
            || x.message
            || x.msg
            || x.detail && _coerceToString(x.detail)
            || x.label_hi
            || x.label_en
            || (x.code ? `Error: ${x.code}` : '')
            || JSON.stringify(x).slice(0, 300)
        );
    }
    return String(x);
}

let _reportedRecently = new Set();
axios.interceptors.response.use(
    (res) => res,
    async (err) => {
        try {
            err.friendlyMessage = await _extractDetail(err);
            err.errorId = err?.response?.data?.error_id || null;
            const status = err?.response?.status || 0;
            // Report unexpected server errors (5xx or network fail) so
            // admin's System Errors feed catches them. Skip auth/route
            // errors and any error that already has server-side error_id
            // (backend already logged it).
            const isServerCrash = status >= 500 || status === 0;
            if (isServerCrash && !err.errorId) {
                const key = `${err?.config?.url}|${err?.message}`;
                if (!_reportedRecently.has(key)) {
                    _reportedRecently.add(key);
                    setTimeout(() => _reportedRecently.delete(key), 30000);
                    try {
                        await axios.post(`${API}/errors/client`, {
                            message: (err.friendlyMessage || err?.message || 'Network error').slice(0, 800),
                            kind:    err?.name || 'AxiosError',
                            path:    err?.config?.url || '',
                            stack:   (err?.stack || '').slice(0, 5000),
                            extra:   {
                                status,
                                method: err?.config?.method,
                                ua: navigator.userAgent.slice(0, 200),
                            },
                        });
                    } catch { /* swallow — never loop on error reporter */ }
                }
            }
        } catch { /* interceptor must never throw */ }
        return Promise.reject(err);
    },
);

export const api = {
    // Applications (customer)
    myApplications: () => axios.get(`${API}/applications`).then(r => r.data),
    createApplication: (payload) => axios.post(`${API}/applications`, payload).then(r => r.data),
    // Admin / vendor / sales manual create (creates customer just-in-time if needed)
    adminCreateApplicationManual: (payload) => axios.post(`${API}/admin/applications`, payload).then(r => r.data),
    adminCreateCustomerManual: (payload) => axios.post(`${API}/admin/customers`, payload).then(r => r.data),
    getApplication: (id) => axios.get(`${API}/applications/${id}`).then(r => r.data),
    updateAppDocuments: (id, payload) => axios.put(`${API}/applications/${id}/documents`, payload).then(r => r.data),

    // Complaints
    myComplaints: () => axios.get(`${API}/complaints`).then(r => r.data),
    createComplaint: (payload) => axios.post(`${API}/complaints`, payload).then(r => r.data),

    // Vendors
    listVendors: () => axios.get(`${API}/vendors`).then(r => r.data),

    // Vendor Messages
    getVendorThread: (vendorId) => axios.get(`${API}/vendor-messages/${vendorId}`).then(r => r.data),
    sendVendorMessage: (vendor_id, text) => axios.post(`${API}/vendor-messages`, { vendor_id, text }).then(r => r.data),

    // Subsidy
    subsidyRates: () => axios.get(`${API}/subsidy-rates`).then(r => r.data),

    // Content
    listContent: () => axios.get(`${API}/content`).then(r => r.data),

    // Quotations (customer)
    myQuotations: () => axios.get(`${API}/quotations`).then(r => r.data),

    // AI
    aiChat: (session_id, message, mode = 'solar') => axios.post(`${API}/ai/chat`, { session_id, message, mode }).then(r => r.data),
    aiHistory: (session_id) => axios.get(`${API}/ai/history`, { params: { session_id } }).then(r => r.data),
    aiClear: (session_id) => axios.delete(`${API}/ai/history`, { params: { session_id } }).then(r => r.data),
    aiAnalyzeRooftop: (image_base64) => axios.post(`${API}/ai/analyze-rooftop`, { image_base64 }, { timeout: 60000 }).then(r => r.data),
    aiCompareRooftops: (images) => axios.post(`${API}/ai/analyze-rooftops`, { images }, { timeout: 90000 }).then(r => r.data),

    // Products (admin catalog)
    adminListProducts: () => axios.get(`${API}/admin/products`).then(r => r.data),
    adminCreateProduct: (p) => axios.post(`${API}/admin/products`, p).then(r => r.data),
    adminUpdateProduct: (id, p) => axios.put(`${API}/admin/products/${id}`, p).then(r => r.data),
    adminDeleteProduct: (id) => axios.delete(`${API}/admin/products/${id}`).then(r => r.data),

    // Attendance summary (admin report)
    adminAttendanceSummary: (month) => axios.get(`${API}/admin/attendance/summary`, { params: month ? { month } : {} }).then(r => r.data),

    // Solar Saathi bookings
    createSaathiBooking: (payload) => axios.post(`${API}/saathi-bookings`, payload).then(r => r.data),
    adminListSaathiBookings: () => axios.get(`${API}/admin/saathi-bookings`).then(r => r.data),
    adminUpdateSaathiBooking: (id, payload) => axios.put(`${API}/admin/saathi-bookings/${id}`, payload).then(r => r.data),
    adminGetSaathiBooking: (id) => axios.get(`${API}/admin/saathi-bookings/${id}`).then(r => r.data),
    adminBulkAssignSaathiBookings: (ids, assigned_to) => axios.post(`${API}/admin/saathi-bookings/bulk-assign`, { ids, assigned_to }).then(r => r.data),
    // Phase 20.B — Unified Saathi + Site Survey booking
    createSaathiSurvey: (payload) => axios.post(`${API}/saathi-survey`, payload).then(r => r.data),
    mySaathiSurvey:   () => axios.get(`${API}/my/saathi-survey`).then(r => r.data),
    adminChangeSaathiBookingVendor: (bid, vendor_id) => axios.put(`${API}/admin/saathi-bookings/${bid}/vendor`, { vendor_id: vendor_id || '' }).then(r => r.data),
    adminAssignSaathiBookingTeam:   (bid, team_ids) => axios.put(`${API}/admin/saathi-bookings/${bid}/assign-team`, { team_ids: team_ids || [] }).then(r => r.data),

    // Solar Saathi self-serve first-login onboarding wizard
    saathiCompleteOnboarding: (payload) => axios.post(`${API}/saathi/onboarding`, payload).then(r => r.data),

    // Live staff location tracking
    reportStaffLocation: (payload) => axios.post(`${API}/staff/location`, payload).then(r => r.data),
    adminStaffLocations: () => axios.get(`${API}/admin/staff/locations`).then(r => r.data),
    adminStaffTrail: (sid, hours = 4) => axios.get(`${API}/admin/staff/${sid}/trail`, { params: { hours } }).then(r => r.data),

    // Performance report (charts + CSV)
    adminReport: (params = {}) => axios.get(`${API}/admin/dashboard/report`, { params }).then(r => r.data),
    adminReportCsvUrl: (params = {}) => {
        const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
        return `${API}/admin/dashboard/report.csv${qs ? '?' + qs : ''}`;
    },
    // Phase 12i: All-Users overview + Multi-sheet Excel
    adminAllUsersOverview: (params = {}) => axios.get(`${API}/admin/dashboard/all-users-overview`, { params }).then(r => r.data),
    adminAllUsersOverviewXlsxUrl: (params = {}) => {
        const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
        return `${API}/admin/dashboard/all-users-overview.xlsx${qs ? '?' + qs : ''}`;
    },

    // Profile management (all roles)
    updateProfile: (payload) => axios.put(`${API}/auth/profile`, payload).then(r => r.data),
    changePassword: (current_password, new_password) => axios.post(`${API}/auth/change-password`, { current_password, new_password }).then(r => r.data),
    adminResetUserPassword: (uid, new_password) => axios.post(`${API}/admin/users/${uid}/reset-password`, { new_password: new_password || null }).then(r => r.data),
    adminBulkResetPassword: (user_ids, notify_via_whatsapp = false) => axios.post(`${API}/admin/users/bulk-reset-password`, { user_ids, notify_via_whatsapp }).then(r => r.data),
    adminSetReportingManager: (uid, reporting_manager_id) => axios.put(`${API}/admin/users/${uid}/reporting-manager`, { reporting_manager_id: reporting_manager_id || null }).then(r => r.data),
    adminListManagers: () => axios.get(`${API}/admin/users/managers`).then(r => r.data),
    adminHierarchy: () => axios.get(`${API}/admin/hierarchy`).then(r => r.data),
    adminMyTeamStats: () => axios.get(`${API}/admin/hierarchy/my-team-stats`).then(r => r.data),
    myTeamExportUrl: (format = 'csv') => `${API}/admin/hierarchy/my-team-export?format=${format}`,
    adminNormalizeDistricts: (apply = false, field = null) => axios.post(`${API}/admin/tools/normalize-districts`, { apply, field }).then(r => r.data),

    // Manager-driven reassignment (hierarchy-checked on backend)
    adminReassignLead: (id, assigned_to, note = '') => axios.put(`${API}/admin/leads/${id}/reassign`, { assigned_to, note }).then(r => r.data),
    adminReassignApplication: (id, assigned_sales_id, note = '') => axios.put(`${API}/admin/applications/${id}/reassign`, { assigned_sales_id, note }).then(r => r.data),
    // Phase 12i: Vendor assignment for applications & customers
    adminAssignApplicationVendor: (id, assigned_vendor_id, note = '') => axios.put(`${API}/admin/applications/${id}/assign-vendor`, { assigned_vendor_id: assigned_vendor_id || null, note }).then(r => r.data),
    adminAssignCustomerVendor: (cid, assigned_vendor_id, note = '') => axios.put(`${API}/admin/customers/${cid}/assign-vendor`, { assigned_vendor_id: assigned_vendor_id || null, note }).then(r => r.data),
    adminAssignCustomerSales: (cid, assigned_sales_id, note = '') => axios.put(`${API}/admin/customers/${cid}/assign-sales`, { assigned_sales_id: assigned_sales_id || null, note }).then(r => r.data),
    adminAssignLeadVendor: (lid, assigned_vendor_id, note = '') => axios.put(`${API}/admin/leads/${lid}/assign-vendor`, { assigned_vendor_id: assigned_vendor_id || null, note }).then(r => r.data),
    adminVendorLoad: () => axios.get(`${API}/admin/vendors/load`).then(r => r.data),
    adminVendorLoadReport: () => axios.get(`${API}/admin/vendors/load-report`).then(r => r.data),
    adminBulkAssignStaffVendor: (ids, assigned_vendor_id) => axios.post(`${API}/admin/staff/bulk-assign-vendor`, { ids, assigned_vendor_id: assigned_vendor_id || null }).then(r => r.data),
    adminBulkAssignLeadsVendor: (ids, assigned_vendor_id) => axios.post(`${API}/admin/leads/bulk-assign-vendor`, { ids, assigned_vendor_id: assigned_vendor_id || null }).then(r => r.data),
    // Phase 51 — Territory-based bulk assign & coverage heatmap.
    adminVendorCoverage: () => axios.get(`${API}/admin/vendor-coverage`).then(r => r.data),
    adminPreviewLeadsByDistrict: (district, only_unassigned = true) =>
        axios.get(`${API}/admin/vendor-coverage/preview-district`, { params: { district, only_unassigned } }).then(r => r.data),
    adminBulkAssignLeadsVendorByDistrict: (payload) =>
        axios.post(`${API}/admin/leads/bulk-assign-vendor-by-district`, payload).then(r => r.data),
    adminBulkAssignApplicationsVendor: (ids, assigned_vendor_id) => axios.post(`${API}/admin/applications/bulk-assign-vendor`, { ids, assigned_vendor_id: assigned_vendor_id || null }).then(r => r.data),
    adminReindexApplicationTenants: () => axios.post(`${API}/admin/applications/reindex-tenants`).then(r => r.data),
    // Phase 47.38 — Repair orphaned vendors + their applications so Care/tenant scope catches them.
    // Pass `claim=true` to adopt any orphan vendor (no resolvable tenant) into the CALLER's tenant —
    // used by co-root admins to fix legacy vendors like "Rajesh Kumar" who were created before
    // Phase 47.33 auto-tagging.
    adminBackfillVendorTenants: ({ claim = false } = {}) =>
        axios.post(`${API}/admin/system/backfill-vendor-tenants${claim ? '?claim=true' : ''}`)
            .then(r => r.data),
    adminBulkAssignLeadsSales: (ids, assigned_sales_id) => axios.post(`${API}/admin/leads/bulk-assign-sales`, { ids, assigned_sales_id: assigned_sales_id || null }).then(r => r.data),
    adminBulkAssignCustomersSales: (ids, assigned_sales_id) => axios.post(`${API}/admin/customers/bulk-assign-sales`, { ids, assigned_sales_id: assigned_sales_id || null }).then(r => r.data),
    adminVendorLoadReportXlsxUrl: () => `${API}/admin/vendors/load-report.xlsx`,
    adminReassignComplaint: (id, assigned_to, note = '') => axios.put(`${API}/admin/complaints/${id}/reassign`, { assigned_to, note }).then(r => r.data),
    adminBulkReassignLeads: (ids, assigned_to, note = '') => axios.post(`${API}/admin/leads/bulk-reassign`, { ids, assigned_to, note }).then(r => r.data),
    adminBulkReassignApplications: (ids, assigned_sales_id, note = '') => axios.post(`${API}/admin/applications/bulk-reassign`, { ids, assigned_sales_id, note }).then(r => r.data),
    adminBulkReassignComplaints: (ids, assigned_to, note = '') => axios.post(`${API}/admin/complaints/bulk-reassign`, { ids, assigned_to, note }).then(r => r.data),
    // Phase 20.A — Admin change complaint vendor + Vendor multi-assign team
    adminChangeComplaintVendor: (cid, vendor_id) => axios.put(`${API}/admin/complaints/${cid}/vendor`, { vendor_id: vendor_id || '' }).then(r => r.data),
    adminAssignComplaintTeam: (cid, team_ids) => axios.put(`${API}/admin/complaints/${cid}/assign-team`, { team_ids: team_ids || [] }).then(r => r.data),
    // Phase 21 — Multi-assign for Leads & Site Surveys (mirror of complaint/saathi APIs)
    adminChangeLeadVendor: (lid, vendor_id) => axios.put(`${API}/admin/leads/${lid}/vendor`, { vendor_id: vendor_id || '' }).then(r => r.data),
    adminAssignLeadTeam:   (lid, team_ids) => axios.put(`${API}/admin/leads/${lid}/assign-team`, { team_ids: team_ids || [] }).then(r => r.data),
    adminChangeSiteSurveyVendor: (sid, vendor_id) => axios.put(`${API}/admin/site-surveys/${sid}/vendor`, { vendor_id: vendor_id || '' }).then(r => r.data),
    adminAssignSiteSurveyTeam:   (sid, team_ids) => axios.put(`${API}/admin/site-surveys/${sid}/assign-team`, { team_ids: team_ids || [] }).then(r => r.data),

    // Staff
    adminListStaff: () => axios.get(`${API}/admin/staff`).then(r => r.data),
    adminCreateStaff: (p) => axios.post(`${API}/admin/staff`, p).then(r => r.data),
    // Phase 12i: Resend/rotate credentials for staff via WhatsApp/SMS
    adminResendStaffCredentials: (sid, new_password = null) =>
        axios.post(`${API}/admin/staff/${sid}/send-credentials-whatsapp`, { new_password: new_password || null }).then(r => r.data),
    adminUpdateStaff: (id, p) => axios.put(`${API}/admin/staff/${id}`, p).then(r => r.data),
    adminDeleteStaff: (id) => axios.delete(`${API}/admin/staff/${id}`).then(r => r.data),
    listRoles: () => axios.get(`${API}/roles`).then(r => r.data),

    // Referrals
    myReferrals: () => axios.get(`${API}/referrals/me`).then(r => r.data),
    createReferral: (p) => axios.post(`${API}/referrals`, p).then(r => r.data),
    adminReferrals: () => axios.get(`${API}/admin/referrals`).then(r => r.data),
    adminUpdateReferral: (id, p) => axios.put(`${API}/admin/referrals/${id}`, p).then(r => r.data),

    // Site surveys
    createSiteSurvey: (p) => axios.post(`${API}/site-surveys`, p).then(r => r.data),
    mySiteSurveys: () => axios.get(`${API}/site-surveys`).then(r => r.data),
    adminSiteSurveys: () => axios.get(`${API}/admin/site-surveys`).then(r => r.data),
    adminUpdateSiteSurvey: (id, p) => axios.put(`${API}/admin/site-surveys/${id}`, p).then(r => r.data),
    adminUploadSurveyPhoto: (sid, phase, data_url, caption='') => axios.post(`${API}/admin/site-surveys/${sid}/photos`, { phase, data_url, caption }).then(r => r.data),
    adminListSurveyPhotos: (sid) => axios.get(`${API}/admin/site-surveys/${sid}/photos`).then(r => r.data),
    adminDeleteSurveyPhoto: (sid, pid) => axios.delete(`${API}/admin/site-surveys/${sid}/photos/${pid}`).then(r => r.data),
    adminApproveSurvey: (sid, approved, remarks='') => axios.post(`${API}/admin/site-surveys/${sid}/approve`, { approved, remarks }).then(r => r.data),
    adminBulkApproveSurveys: (ids, approved, remarks='') => axios.post(`${API}/admin/site-surveys/bulk-approve`, { ids, approved, remarks }).then(r => r.data),
    adminSurveyLeaderboard: () => axios.get(`${API}/admin/site-surveys/leaderboard`).then(r => r.data),
    getLandingContent: () => axios.get(`${API}/landing-content`).then(r => r.data),
    adminGetLandingContent: () => axios.get(`${API}/admin/landing-content`).then(r => r.data),
    adminSaveLandingContent: (content, publish=false) => axios.put(`${API}/admin/landing-content`, { content, publish }).then(r => r.data),
    adminUploadLandingImage: (data_url, label='') => axios.post(`${API}/admin/landing-content/upload-image`, { data_url, label }).then(r => r.data),

    // Generation logs
    createGenLog: (p) => axios.post(`${API}/generation-logs`, p).then(r => r.data),
    listGenLogs: () => axios.get(`${API}/generation-logs`).then(r => r.data),
    deleteGenLog: (id) => axios.delete(`${API}/generation-logs/${id}`).then(r => r.data),

    // Lead calls
    logLeadCall: (lid, payload) => axios.post(`${API}/admin/leads/${lid}/calls`, payload).then(r => r.data),
    listLeadCalls: (lid) => axios.get(`${API}/admin/leads/${lid}/calls`).then(r => r.data),
    dueFollowups: (within_hours = 48) => axios.get(`${API}/admin/leads/follow-ups/due`, { params: { within_hours } }).then(r => r.data),

    // Team dashboard
    teamDashboard: (params = {}) => axios.get(`${API}/admin/dashboard/team`, { params }).then(r => r.data),

    // Lead remarks
    addLeadRemark: (lid, p) => axios.post(`${API}/admin/leads/${lid}/remarks`, p).then(r => r.data),
    listLeadRemarks: (lid) => axios.get(`${API}/admin/leads/${lid}/remarks`).then(r => r.data),

    // WhatsApp
    getWelcomeWhatsapp: () => axios.get(`${API}/whatsapp/welcome`).then(r => r.data),
    adminWhatsAppOnboarding: () => axios.get(`${API}/admin/whatsapp-onboarding`).then(r => r.data),

    // EMI config
    emiConfig: () => axios.get(`${API}/emi/config`).then(r => r.data),

    // Export URLs (frontend triggers a fetch download with auth header)
    exportUrl: (report, format) => `${API}/admin/export/${report}?format=${format}`,

    // ---------- Admin ----------
    adminDashboard: () => axios.get(`${API}/admin/dashboard`).then(r => r.data),

    adminApplications: () => axios.get(`${API}/admin/applications`).then(r => r.data),
    adminUpdateAppStatus: (id, status, note) => axios.put(`${API}/admin/applications/${id}/status`, { status, note }).then(r => r.data),
    adminUpdateAppPaymentStatus: (id, payment_status, note = '') => axios.put(`${API}/admin/applications/${id}/payment-status`, { payment_status, note }).then(r => r.data),
    adminPaymentStatusSummary: () => axios.get(`${API}/admin/applications/payment-status-summary`).then(r => r.data),
    // Vendor self-view of own Khata Book (read-only)
    vendorMyKhata: (params = {}) => {
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
        return axios.get(`${API}/vendor/khata${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    vendorMyKhataStatementPdf: (params = {}) => {
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
        return axios.get(`${API}/vendor/khata/statement.pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    adminVendorKhataStatementPdf: (vendorId, params = {}) => {
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
        return axios.get(`${API}/admin/vendor-ledger/${vendorId}/statement.pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    vendorMyKhataXlsx: (params = {}) => {
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
        return axios.get(`${API}/vendor/khata/export.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    adminGetApplication: (id) => axios.get(`${API}/admin/applications/${id}`).then(r => r.data),
    adminEditApplication: (id, payload) => axios.put(`${API}/admin/applications/${id}`, payload).then(r => r.data),
    adminRevertApplication: (id, target_status, reason) => axios.post(`${API}/admin/applications/${id}/revert`, { target_status, reason }).then(r => r.data),

    // Phase 47.47 — Per-installment save + lock + unlock request flow.
    adminInstallmentSave:            (aid, payload) => axios.post(`${API}/admin/applications/${aid}/installment-save`, payload).then(r => r.data),
    adminInstallmentUnlockRequest:   (aid, key, reason) => axios.post(`${API}/admin/applications/${aid}/installment-unlock-request`, { key, reason }).then(r => r.data),
    adminInstallmentUnlockApprove:   (aid, reqId, approve = true) => axios.post(`${API}/admin/applications/${aid}/installment-unlock/${reqId}?approve=${approve ? 'true' : 'false'}`).then(r => r.data),
    // Phase 47.52 — Bulk approve / reject unlock requests.
    adminInstallmentUnlockBulk:      (payload) => axios.post(`${API}/admin/applications/installment-unlock-bulk`, payload).then(r => r.data),
    adminInstallmentSummary:         (aid) => axios.get(`${API}/admin/applications/${aid}/installment-summary`).then(r => r.data),

    // Phase 47.49 — Backfill projects for old approved applications.
    adminBackfillProjectsFromApps:   () => axios.post(`${API}/admin/projects/backfill-from-applications`).then(r => r.data),

    adminComplaints: () => axios.get(`${API}/admin/complaints`).then(r => r.data),
    // Phase 37 — Staff-created complaint (Admin, Care, Vendor, Sales, Installation, Saathi).
    // Payload accepts either `customer_id` (link existing) OR raw `customer_name`+`customer_mobile`.
    adminCreateComplaint: (p) => axios.post(`${API}/admin/complaints`, p).then(r => r.data),
    adminUpdateComplaint: (id, payload) => axios.put(`${API}/admin/complaints/${id}/status`, payload).then(r => r.data),
    scheduleComplaintVisit: (id, p) => axios.post(`${API}/admin/complaints/${id}/schedule-visit`, p).then(r => r.data),
    closeComplaint: (id, p) => axios.post(`${API}/admin/complaints/${id}/close`, p).then(r => r.data),
    requestReschedule: (id, p) => axios.post(`${API}/admin/complaints/${id}/reschedule-request`, p).then(r => r.data),
    decideReschedule: (id, p) => axios.post(`${API}/admin/complaints/${id}/reschedule-decision`, p).then(r => r.data),
    pendingRescheduleCount: () => axios.get(`${API}/admin/complaints/pending-reschedules/count`).then(r => r.data),
    sendOtpReminders: () => axios.post(`${API}/admin/complaints/otp-reminders/send`).then(r => r.data),

    adminLeads: () => axios.get(`${API}/admin/leads`).then(r => r.data),
    adminCreateLead: (p) => axios.post(`${API}/admin/leads`, p).then(r => r.data),
    adminUpdateLead: (id, p) => axios.put(`${API}/admin/leads/${id}`, p).then(r => r.data),
    adminDeleteLead: (id) => axios.delete(`${API}/admin/leads/${id}`).then(r => r.data),
    // Phase 36 — Merge duplicate leads (Admin + Customer Care only).
    // Backend groups by last-10-digit mobile, promotes the oldest lead as
    // target, and archives (`status='Archived'`) the rest.
    mergeLeadDuplicates: (id) => axios.post(`${API}/admin/leads/${id}/merge-duplicates`).then(r => r.data),

    adminQuotations: () => axios.get(`${API}/admin/quotations`).then(r => r.data),
    adminCreateQuotation: (p) => axios.post(`${API}/admin/quotations`, p).then(r => r.data),
    // Draft preview — returns Blob (PDF) for inline modal display
    adminPreviewQuotationDraft: (payload) => axios.post(
        `${API}/admin/quotations/preview.pdf`,
        payload,
        { responseType: 'blob' },
    ).then(r => r.data),

    // Phase 12: Quotation status actions + Sale Invoice
    adminMarkQuotationStatus: (qid, status, note = '') => axios.post(
        `${API}/admin/quotations/${qid}/mark-status`,
        { status, note },
    ).then(r => r.data),
    adminReviseQuotation: (qid, payload) => axios.post(
        `${API}/admin/quotations/${qid}/revise`, payload,
    ).then(r => r.data),
    adminConvertQuotationToInvoice: (qid, payload) => axios.post(
        `${API}/admin/quotations/${qid}/convert-to-invoice`, payload,
    ).then(r => r.data),
    adminDownloadSaleInvoicePdf: (iid) => axios.get(
        `${API}/admin/sale-invoices/${iid}/pdf`, { responseType: 'blob' },
    ).then(r => r.data),
    adminListSaleInvoices: () => axios.get(`${API}/admin/sale-invoices`).then(r => r.data),
    // Phase 12c: Invoice Voiding + Pay Link
    adminVoidSaleInvoice: (iid, reason) => axios.post(
        `${API}/admin/sale-invoices/${iid}/void`, { reason },
    ).then(r => r.data),
    // Phase 13: Hard-delete a Voided sale invoice (admin only, permanent)
    adminDeleteVoidedSaleInvoice: (iid) => axios.delete(
        `${API}/admin/sale-invoices/${iid}`,
    ).then(r => r.data),
    // One-sweep void-and-delete for a batch of invoice ids. Returns
    // per-id summary of deleted / skipped / errors so UI can toast counts.
    adminBulkVoidDeleteSaleInvoices: (ids, reason) => axios.post(
        `${API}/admin/sale-invoices/bulk-void-delete`, { ids, reason },
    ).then(r => r.data),
    adminArchiveProject: (pid) => axios.put(
        `${API}/admin/projects/${pid}/archive`,
    ).then(r => r.data),
    adminUnarchiveProject: (pid) => axios.put(
        `${API}/admin/projects/${pid}/unarchive`,
    ).then(r => r.data),
    adminDeleteProject: (pid) => axios.delete(
        `${API}/admin/projects/${pid}`,
    ).then(r => r.data),
    // Vendor + Team assignment on Projects (mirrors Leads / Site Surveys /
    // Complaints). Admin/Care use `changeProjectVendor`; the resulting
    // Vendor uses `assignProjectTeam` to delegate to their own reporting
    // staff (sales + installation + saathi).
    changeProjectVendor: (pid, vendor_id) => axios.put(
        `${API}/admin/projects/${pid}/vendor`, { vendor_id: vendor_id || null },
    ).then(r => r.data),
    assignProjectTeam: (pid, team_ids) => axios.put(
        `${API}/admin/projects/${pid}/assign-team`, { team_ids: team_ids || [] },
    ).then(r => r.data),
    // Phase 30 — Team Data Peek for Vendor dashboard tile.
    vendorTeamPeek: () => axios.get(`${API}/vendor/team-peek`).then(r => r.data),
    // Phase 32 — Care permission toggle (admin only).
    getCarePermissions: () => axios.get(`${API}/admin/care-permissions`).then(r => r.data),
    setCarePermissions: (payload) => axios.put(`${API}/admin/care-permissions`, payload).then(r => r.data),
    // Phase 33 — Care audit trail feed.
    adminVendorAssignAudit: (params = {}) => axios.get(
        `${API}/admin/audit-log/vendor-assigns`, { params },
    ).then(r => r.data),
    adminGenerateInvoicePayLink: (iid, payload = {}) => axios.post(
        `${API}/admin/sale-invoices/${iid}/generate-pay-link`, payload,
    ).then(r => r.data),
    // Phase 12d: Manual create + edit
    adminCreateSaleInvoice: (payload) => axios.post(
        `${API}/admin/sale-invoices`, payload,
    ).then(r => r.data),
    adminUpdateSaleInvoice: (iid, payload) => axios.put(
        `${API}/admin/sale-invoices/${iid}`, payload,
    ).then(r => r.data),
    // Phase 12e: Manual Payments + Bulk delete + Receipt PDFs
    adminRecordInvoicePayment: (iid, payload) => axios.post(
        `${API}/admin/sale-invoices/${iid}/record-payment`, payload,
    ).then(r => r.data),
    // Phase 47 — Record advance / token payment against a quotation.
    adminRecordQuotationPayment: (qid, payload) => axios.post(
        `${API}/admin/quotations/${qid}/record-payment`, payload,
    ).then(r => r.data),
    adminDownloadInvoiceReceiptPdf: (iid, paymentId) => axios.get(
        `${API}/admin/sale-invoices/${iid}/receipt-pdf`,
        { responseType: 'blob', params: paymentId ? { payment_id: paymentId } : {} },
    ).then(r => r.data),
    // Phase 47 — Advance Payment Receipt for Quotations
    // Phase 47.58 — Optional `paymentId` picks a specific payment entry.
    adminDownloadQuotationReceiptPdf: (qid, paymentId) => axios.get(
        `${API}/admin/quotations/${qid}/receipt-pdf`,
        { responseType: 'blob', params: paymentId ? { payment_id: paymentId } : {} },
    ).then(r => r.data),
    downloadMyQuotationReceiptPdf: (qid) => axios.get(
        `${API}/quotations/${qid}/receipt-pdf`, { responseType: 'blob' },
    ).then(r => r.data),
    adminBulkDeleteSaleInvoices: (ids) => axios.post(
        `${API}/admin/sale-invoices/bulk-delete`, { ids },
    ).then(r => r.data),
    // Phase 12f: Cash Register
    adminCashRegister: (dateFrom, dateTo) => axios.get(`${API}/admin/cash-register`, {
        params: { date_from: dateFrom, date_to: dateTo || dateFrom },
    }).then(r => r.data),
    adminCashRegisterPdf: (dateFrom, dateTo) => axios.get(`${API}/admin/cash-register/pdf`, {
        params: { date_from: dateFrom, date_to: dateTo || dateFrom },
        responseType: 'blob',
    }).then(r => r.data),
    adminCashDepositSlipPdf: (payload) => axios.post(
        `${API}/admin/cash-register/deposit-slip/pdf`, payload,
        { responseType: 'blob' },
    ).then(r => r.data),
    // Phase 12g: Transaction actions
    adminEditInvoicePayment: (iid, pid, payload) => axios.put(
        `${API}/admin/sale-invoices/${iid}/payments/${pid}`, payload,
    ).then(r => r.data),
    // Phase 47.42: Edit an existing quotation payment (mirror of invoice).
    adminEditQuotationPayment: (qid, pid, payload) => axios.put(
        `${API}/admin/quotations/${qid}/payments/${pid}`, payload,
    ).then(r => r.data),
    // Phase 47.43: Void (soft-delete) a payment entry with a reason for audit.
    adminVoidQuotationPayment: (qid, pid, reason) => axios.delete(
        `${API}/admin/quotations/${qid}/payments/${pid}`, { data: { reason: reason || '' } },
    ).then(r => r.data),
    adminVoidInvoicePayment: (iid, pid, reason) => axios.delete(
        `${API}/admin/sale-invoices/${iid}/payments/${pid}`, { data: { reason: reason || '' } },
    ).then(r => r.data),
    // Phase 47.44 — Payment change request queue (approval workflow)
    adminListPaymentChangeRequests: (params) => axios.get(
        `${API}/admin/payment-change-requests`, { params: params || {} },
    ).then(r => r.data),
    adminApprovePaymentChangeRequest: (rid, note) => axios.post(
        `${API}/admin/payment-change-requests/${rid}/approve`, { note: note || '' },
    ).then(r => r.data),
    adminRejectPaymentChangeRequest: (rid, note) => axios.post(
        `${API}/admin/payment-change-requests/${rid}/reject`, { note: note || '' },
    ).then(r => r.data),
    adminCancelPaymentChangeRequest: (rid) => axios.delete(
        `${API}/admin/payment-change-requests/${rid}`,
    ).then(r => r.data),
    // Phase 47.47 — Vendor "My Firm" profile (own-branded quotations / invoices)
    vendorGetMyFirm: () => axios.get(`${API}/vendor/my-firm`).then(r => r.data),
    vendorUpdateMyFirm: (p) => axios.put(`${API}/vendor/my-firm`, p).then(r => r.data),
    // Phase 47.53 — Preview sample invoice PDF with the vendor's (possibly
    // unsaved) firm details. Returns a Blob so the caller can open it in a
    // new browser tab via URL.createObjectURL().
    vendorPreviewMyFirmPdf: (p) => axios.post(
        `${API}/vendor/my-firm/preview-pdf`, p, { responseType: 'blob' },
    ).then(r => r.data),
    // Phase 47.54 — Firm audit log (who/what/when changed on My Firm).
    vendorGetMyFirmAuditLog: () => axios.get(
        `${API}/vendor/my-firm/audit-log`,
    ).then(r => r.data),
    adminBulkDeleteInvoicePayments: (items) => axios.post(
        `${API}/admin/sale-invoices/payments/bulk-delete`, { items },
    ).then(r => r.data),
    adminListDepositSlips: () => axios.get(
        `${API}/admin/cash-register/deposit-slips`,
    ).then(r => r.data),
    adminReprintDepositSlip: (sid) => axios.get(
        `${API}/admin/cash-register/deposit-slips/${sid}/pdf`, { responseType: 'blob' },
    ).then(r => r.data),
    adminDeleteDepositSlip: (sid) => axios.delete(
        `${API}/admin/cash-register/deposit-slips/${sid}`,
    ).then(r => r.data),
    // Customer receipt
    downloadMyInvoiceReceiptPdf: (iid) => axios.get(
        `${API}/sale-invoices/${iid}/receipt-pdf`, { responseType: 'blob' },
    ).then(r => r.data),
    // Customer accept/reject
    customerQuotationAction: (qid, action) => axios.post(
        `${API}/quotations/${qid}/customer-action`, { action },
    ).then(r => r.data),

    // Customer-facing sale invoices (Phase 12b)
    mySaleInvoices: () => axios.get(`${API}/sale-invoices`).then(r => r.data),
    downloadMySaleInvoicePdf: (iid) => axios.get(
        `${API}/sale-invoices/${iid}/pdf`, { responseType: 'blob' },
    ).then(r => r.data),

    // PMSGY duplicate live check
    adminCheckPmsgyAppNo: (value, exclude_id = null) => axios.get(
        `${API}/admin/pmsgy-app-no/check`,
        { params: exclude_id ? { value, exclude_id } : { value } },
    ).then(r => r.data),

    // Razorpay
    razorpayConfig: () => axios.get(`${API}/razorpay/config`).then(r => r.data),
    razorpaySurchargePreview: (base_paise, flow = 'customer_application') => axios.get(`${API}/razorpay/surcharge/preview`, { params: { base_paise, flow } }).then(r => r.data),
    adminSurchargeReport: (months = 12) => axios.get(`${API}/admin/razorpay/surcharge/report`, { params: { months } }).then(r => r.data),
    adminRazorpayWebhookUrl: () => axios.get(`${API}/admin/razorpay/webhook-url`).then(r => r.data),
    adminRazorpayWebhookEvents: (limit = 20) => axios.get(`${API}/admin/razorpay/webhook-events`, { params: { limit } }).then(r => r.data),
    razorpayOrder: (payload) => axios.post(`${API}/razorpay/order`, payload).then(r => r.data),
    razorpayBookingOrder: (payload) => axios.post(`${API}/razorpay/booking-order`, payload).then(r => r.data),
    razorpayVerify: (payload) => axios.post(`${API}/razorpay/verify`, payload).then(r => r.data),
    adminRazorpaySettings: () => axios.get(`${API}/admin/razorpay/settings`).then(r => r.data),
    adminRazorpaySaveSettings: (payload) => axios.put(`${API}/admin/razorpay/settings`, payload).then(r => r.data),
    adminRazorpayPaymentLink: (payload) => axios.post(`${API}/admin/razorpay/payment-link`, payload).then(r => r.data),
    adminRazorpayOrders: (limit = 100) => axios.get(`${API}/admin/razorpay/orders`, { params: { limit } }).then(r => r.data),
    adminRazorpayPaymentLinks: (limit = 100) => axios.get(`${API}/admin/razorpay/payment-links`, { params: { limit } }).then(r => r.data),
    adminRazorpayTestConnection: () => axios.post(`${API}/admin/razorpay/test-connection`).then(r => r.data),
    razorpayMyLatestFailed: () => axios.get(`${API}/razorpay/my-latest-failed`).then(r => r.data),
    adminRazorpayRefund: (payload) => axios.post(`${API}/admin/razorpay/refund`, payload).then(r => r.data),
    adminRazorpayAnalytics: (days = 30) => axios.get(`${API}/admin/razorpay/analytics`, { params: { days } }).then(r => r.data),
    adminRazorpayQrPosterUrl: (application_id, amount, payment_type = 'installment') =>
        `${API}/admin/razorpay/qr-poster.pdf?application_id=${encodeURIComponent(application_id)}&amount=${amount}&payment_type=${encodeURIComponent(payment_type)}`,
    // Bulk poster: merge up to 50 A4 QR posters into a single downloadable PDF.
    // Accepts an items[] array of { application_id, amount, payment_type }.
    adminRazorpayQrPosterBulk: (items) =>
        axios.post(`${API}/admin/razorpay/qr-poster-bulk.pdf`, { items }, { responseType: 'blob' })
            .then(r => r.data),

    adminVendors: () => axios.get(`${API}/admin/vendors`).then(r => r.data),
    adminCreateVendor: (p) => axios.post(`${API}/admin/vendors`, p).then(r => r.data),
    adminUpdateVendor: (id, p) => axios.put(`${API}/admin/vendors/${id}`, p).then(r => r.data),
    adminDeleteVendor: (id) => axios.delete(`${API}/admin/vendors/${id}`).then(r => r.data),
    adminEnableVendorLogin: (id, password = null) => axios.post(`${API}/admin/vendors/${id}/enable-login`, { password }).then(r => r.data),
    adminBulkEnableVendorLogins: () => axios.post(`${API}/admin/vendors/bulk-enable-login`).then(r => r.data),
    adminSyncVendorRatings: () => axios.post(`${API}/admin/vendors/sync-ratings`).then(r => r.data),
    adminListSaathiCommissions: (params = {}) => axios.get(`${API}/admin/saathi-commissions`, { params }).then(r => r.data),
    adminMarkCommissionsPaid: (ids) => axios.post(`${API}/admin/saathi-commissions/mark-paid`, { ids }).then(r => r.data),
    saathiPayoutCsvUrl: () => `${API}/admin/saathi-commissions/payout-batch.csv`,
    adminSaathiPayoutSummary: () => axios.get(`${API}/admin/saathi-commissions/payout-summary`).then(r => r.data),
    adminRunVendorReportCards: (payload) => axios.post(`${API}/admin/vendors/report-cards/run`, payload).then(r => r.data),
    adminSuggestSaathiTerritory: (limit = 5) => axios.get(`${API}/admin/saathi/suggest-territory`, { params: { limit } }).then(r => r.data),
    adminTerritoryHeatmap: (days = 30) => axios.get(`${API}/admin/territory/heatmap`, { params: { days } }).then(r => r.data),

    adminCustomers: () => axios.get(`${API}/admin/customers`).then(r => r.data),
    adminCustomer: (id) => axios.get(`${API}/admin/customers/${id}`).then(r => r.data),
    adminUpdateCustomer: (id, p) => axios.put(`${API}/admin/customers/${id}`, p).then(r => r.data),
    adminDeleteCustomer: (id) => axios.delete(`${API}/admin/customers/${id}`).then(r => r.data),
    adminBulkDeleteCustomers: (ids) => axios.post(`${API}/admin/customers/bulk-delete`, { ids }).then(r => r.data),

    adminSubsidyCreate: (p) => axios.post(`${API}/admin/subsidy-rates`, p).then(r => r.data),
    adminSubsidyUpdate: (id, p) => axios.put(`${API}/admin/subsidy-rates/${id}`, p).then(r => r.data),
    adminSubsidyDelete: (id) => axios.delete(`${API}/admin/subsidy-rates/${id}`).then(r => r.data),

    adminContentCreate: (p) => axios.post(`${API}/admin/content`, p).then(r => r.data),
    adminContentUpdate: (id, p) => axios.put(`${API}/admin/content/${id}`, p).then(r => r.data),
    adminContentDelete: (id) => axios.delete(`${API}/admin/content/${id}`).then(r => r.data),

    adminVendorThreads: () => axios.get(`${API}/admin/vendor-messages`).then(r => r.data),
    adminVendorReply: (thread_id, text, sender = 'vendor') => axios.post(`${API}/admin/vendor-messages/reply`, { thread_id, text, sender }).then(r => r.data),

    // Notifications
    adminNotifSettings: () => axios.get(`${API}/admin/notifications/settings`).then(r => r.data),
    adminSaveNotifSettings: (p) => axios.put(`${API}/admin/notifications/settings`, p).then(r => r.data),
    adminNotifTest: (channel, to, message) => axios.post(`${API}/admin/notifications/test`, { channel, to, message }).then(r => r.data),
    adminNotifTestHistory: (channel, limit = 3) => axios.get(`${API}/admin/notifications/test-history`, { params: { channel, limit } }).then(r => r.data),
    adminSendWhatsApp: (payload) => axios.post(`${API}/admin/notifications/whatsapp/send`, payload).then(r => r.data),
    adminBroadcastWhatsApp: (payload) => axios.post(`${API}/admin/notifications/whatsapp/broadcast`, payload).then(r => r.data),
    adminListScheduledBroadcasts: (params = {}) => axios.get(`${API}/admin/notifications/whatsapp/scheduled`, { params }).then(r => r.data),
    adminCancelScheduledBroadcast: (id) => axios.delete(`${API}/admin/notifications/whatsapp/scheduled/${id}`).then(r => r.data),
    adminListWappTemplates: () => axios.get(`${API}/admin/wapp/templates`).then(r => r.data),
    adminCreateWappTemplate: (p) => axios.post(`${API}/admin/wapp/templates`, p).then(r => r.data),
    adminUpdateWappTemplate: (id, p) => axios.put(`${API}/admin/wapp/templates/${id}`, p).then(r => r.data),
    adminDeleteWappTemplate: (id) => axios.delete(`${API}/admin/wapp/templates/${id}`).then(r => r.data),
    adminBulkUploadWappTemplates: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return axios.post(`${API}/admin/wapp/templates/bulk-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
    },
    adminDownloadWappTemplatesSampleXlsx: () => axios.get(`${API}/admin/wapp/templates/sample.xlsx`, { responseType: 'blob' }).then(r => r.data),
    adminGetSlaConfig: () => axios.get(`${API}/admin/sla-config`).then(r => r.data),
    adminSaveSlaConfig: (payload) => axios.put(`${API}/admin/sla-config`, payload).then(r => r.data),

    adminNotifLogs: (limit = 200) => axios.get(`${API}/admin/notifications/logs`, { params: { limit } }).then(r => r.data),
    adminNotifDeliveryEvents: (limit = 100) => axios.get(`${API}/admin/notifications/delivery-events`, { params: { limit } }).then(r => r.data),

    // App version / OTA update prompt
    appVersionInfo: () => axios.get(`${API}/app/version-info`).then(r => r.data),
    adminSetAppVersion: (payload) => axios.put(`${API}/admin/app/version-info`, payload).then(r => r.data),
    // Phase 12i: Install-prompt analytics
    installPromptEvent: (payload) => axios.post(`${API}/public/install-prompt/event`, payload).then(r => r.data),
    adminInstallPromptStats: (params = {}) => axios.get(`${API}/admin/install-prompt/stats`, { params }).then(r => r.data),
    appHeartbeat: (payload) => axios.post(`${API}/app/heartbeat`, payload).then(r => r.data),
    adminAppVersionStats: (days = 30) => axios.get(`${API}/admin/app/version-stats`, { params: { days } }).then(r => r.data),
    adminAppDevices: (params = {}) => axios.get(`${API}/admin/app/devices`, { params }).then(r => r.data),

    // Bill OCR
    billOCR: (image_base64) => axios.post(`${API}/customer/bill-ocr`, { image_base64 }).then(r => r.data),
    adminRunMonthlyGenReminder: () => axios.post(`${API}/admin/notifications/run-monthly-generation-reminder`).then(r => r.data),

    // Subsidy tracker
    adminSubsidyTracker: (status) => axios.get(`${API}/admin/subsidy-tracker`, { params: status ? { status } : {} }).then(r => r.data),
    adminSubsidyProof: (userId) => axios.get(`${API}/admin/subsidy-tracker/${userId}/proof`).then(r => r.data),
    adminVerifySubsidyProof: (userId, verified = true) => axios.post(`${API}/admin/subsidy-tracker/${userId}/verify`, { verified }).then(r => r.data),
    // Central/State split — mark one leg of subsidy as received/pending (admin action)
    adminMarkSubsidyPart: (userId, part, received = true) => axios.post(`${API}/admin/subsidy-tracker/${userId}/mark-part`, { part, received }).then(r => r.data),
    // Customer self-service equivalents
    customerSubsidyStatus: () => axios.get(`${API}/customer/subsidy/status`).then(r => r.data),
    customerMarkSubsidyPart: (part, received = true) => axios.post(`${API}/customer/subsidy/mark-part`, { part, received }).then(r => r.data),
    // Password policy — admin config for min length + mandatory change on first login
    adminGetPasswordPolicy: () => axios.get(`${API}/admin/password-policy`).then(r => r.data),
    adminUpdatePasswordPolicy: (payload) => axios.put(`${API}/admin/password-policy`, payload).then(r => r.data),
    publicPasswordPolicy: () => axios.get(`${API}/auth/password-policy`).then(r => r.data),

    // WhatsApp Web (Personal) session
    waWebInit: () => axios.post(`${API}/admin/notifications/wa-web/init`).then(r => r.data),
    waWebPair: (phone) => axios.post(`${API}/admin/notifications/wa-web/pair`, { phone }).then(r => r.data),
    waWebConfirm: (phone, linked_name) => axios.post(`${API}/admin/notifications/wa-web/confirm`, { phone, linked_name }).then(r => r.data),
    waWebDisconnect: () => axios.post(`${API}/admin/notifications/wa-web/disconnect`).then(r => r.data),
    waWebStatus: () => axios.get(`${API}/admin/notifications/wa-web/status`).then(r => r.data),

    // Payments (per-application installment tracking)
    adminListPayments: (appId) => axios.get(`${API}/admin/applications/${appId}/payments`).then(r => r.data),
    adminAddPayment: (appId, p) => axios.post(`${API}/admin/applications/${appId}/payments`, p).then(r => r.data),
    adminGetCustomerCredentials: (cid) => axios.get(`${API}/admin/customers/${cid}/credentials`).then(r => r.data),

    // Per-user Service Management — quick reset back to role default catalog.
    // Used by the "Reset Services" button on AdminCustomers when a customer
    // (e.g. mobile 9140358667) is missing tiles on their Services page.
    adminGetUserServices:   (uid) => axios.get(`${API}/admin/users/${uid}/services`).then(r => r.data),
    adminResetUserServices: (uid) => axios.post(`${API}/admin/users/${uid}/services/reset-to-default`).then(r => r.data),

    // Generic bulk delete endpoints
    adminBulkDeleteApplications: (ids) => axios.post(`${API}/admin/applications/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteComplaints: (ids) => axios.post(`${API}/admin/complaints/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteQuotations: (ids) => axios.post(`${API}/admin/quotations/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteSiteSurveys: (ids) => axios.post(`${API}/admin/site-surveys/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteSaathiBookings: (ids) => axios.post(`${API}/admin/saathi-bookings/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteVendors: (ids) => axios.post(`${API}/admin/vendors/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteStaff: (ids) => axios.post(`${API}/admin/staff/bulk-delete`, { ids }).then(r => r.data),
    adminBulkDeleteAttendance: (ids) => axios.post(`${API}/admin/attendance/bulk-delete`, { ids }).then(r => r.data),
    adminResetAttendanceReport: (payload) => axios.post(`${API}/admin/attendance/reset-report`, payload).then(r => r.data),
    adminBulkDeletePayments: (items) => axios.post(`${API}/admin/payments/bulk-delete`, { items }).then(r => r.data),
    adminBulkDeleteReferrals: (ids) => axios.post(`${API}/admin/referrals/bulk-delete`, { ids }).then(r => r.data),
    adminRestoreDocs: (entity, docs) => axios.post(`${API}/admin/restore`, { entity, docs }).then(r => r.data),

    adminEditPayment: (appId, pid, p) => axios.put(`${API}/admin/applications/${appId}/payments/${pid}`, p).then(r => r.data),
    adminDeletePayment: (appId, pid) => axios.delete(`${API}/admin/applications/${appId}/payments/${pid}`).then(r => r.data),
    adminPaymentsReport: (params = {}) => axios.get(`${API}/admin/reports/payments`, { params }).then(r => r.data),
    // Phase 47.34 — Server-generated CSV + PDF exports. Uses the same
    // filter query-params as the JSON list so the download always mirrors
    // exactly what the user sees on-screen.
    adminPaymentsReportCsvUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/payments.csv${qs ? `?${qs}` : ''}`;
    },
    adminPaymentsReportPdfUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/payments.pdf${qs ? `?${qs}` : ''}`;
    },
    // Phase 47.45 — Per-application Installment Schedule report (planned + received breakdown).
    adminInstallmentScheduleReport: (params = {}) => axios.get(`${API}/admin/reports/installment-schedule`, { params }).then(r => r.data),
    adminInstallmentScheduleCsvUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/installment-schedule.csv${qs ? `?${qs}` : ''}`;
    },
    // Phase 47.46 — Flat (row-per-installment) CSV — every installment of every
    // application in one sheet so admins can pivot / age directly in Excel.
    adminInstallmentScheduleFlatCsvUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/installment-schedule-flat.csv${qs ? `?${qs}` : ''}`;
    },
    // Phase 47.47 — Native XLSX + PDF exports of the same bulk data.
    adminInstallmentScheduleFlatXlsxUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/installment-schedule-flat.xlsx${qs ? `?${qs}` : ''}`;
    },
    adminInstallmentScheduleFlatPdfUrl: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/installment-schedule-flat.pdf${qs ? `?${qs}` : ''}`;
    },
    // Phase 47.55 — Vendor-wise ledger (JSON + CSV + XLSX + PDF)
    adminVendorLedger:            (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/reports/vendor-ledger${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    adminVendorLedgerCsvUrl:      (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/vendor-ledger.csv${qs ? `?${qs}` : ''}`;
    },
    adminVendorLedgerXlsxUrl:     (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/vendor-ledger.xlsx${qs ? `?${qs}` : ''}`;
    },
    adminVendorLedgerPdfUrl:      (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return `${API}/admin/reports/vendor-ledger.pdf${qs ? `?${qs}` : ''}`;
    },
    // Phase 50 — Khata Book style vendor ledger (per-vendor deep-dive with
    // manual IN/OUT entries + running balance).
    adminKhataSummary: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/vendor-ledger/summary${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    adminKhataDetail: (vendorId, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/vendor-ledger/${vendorId}${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    adminKhataAddEntry: (vendorId, payload) =>
        axios.post(`${API}/admin/vendor-ledger/${vendorId}/entries`, payload).then(r => r.data),
    adminKhataUpdateEntry: (entryId, payload) =>
        axios.put(`${API}/admin/vendor-ledger/entries/${entryId}`, payload).then(r => r.data),
    adminKhataDeleteEntry: (entryId, reason = '') =>
        axios.delete(`${API}/admin/vendor-ledger/entries/${entryId}`, { data: { reason } }).then(r => r.data),
    adminKhataRestoreEntry: (entryId) =>
        axios.post(`${API}/admin/vendor-ledger/entries/${entryId}/restore`).then(r => r.data),
    // Phase 50.5 — Care team delete approval workflow
    adminKhataApprovalsList: (status = 'pending') =>
        axios.get(`${API}/admin/khata-approvals?status_filter=${status}`).then(r => r.data),
    adminKhataApprovalApprove: (approvalId, reason = '') =>
        axios.post(`${API}/admin/khata-approvals/${approvalId}/approve`, { reason }).then(r => r.data),
    adminKhataApprovalReject: (approvalId, reason = '') =>
        axios.post(`${API}/admin/khata-approvals/${approvalId}/reject`, { reason }).then(r => r.data),
    adminKhataDetailXlsx: (vendorId, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/vendor-ledger/${vendorId}/export.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    adminKhataSummaryXlsx: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/vendor-ledger-summary/export.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    adminKhataVoucherPdf: (entryId) =>
        axios.get(`${API}/admin/vendor-ledger/entries/${entryId}/voucher.pdf`, { responseType: 'blob' }).then(r => r.data),
    adminKhataVoucherPdfUrl: (entryId) =>
        `${API}/admin/vendor-ledger/entries/${entryId}/voucher.pdf`,

    // Phase 51 — Customer Khata Book (mirrors Vendor Khata Book but grouped
    // by customer's normalized 10-digit mobile).
    adminCustomerKhataSummary: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/customer-ledger/summary${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    adminCustomerKhataDetail: (customerKey, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/customer-ledger/${encodeURIComponent(customerKey)}${qs ? `?${qs}` : ''}`).then(r => r.data);
    },
    adminCustomerKhataAddEntry: (customerKey, payload) =>
        axios.post(`${API}/admin/customer-ledger/${encodeURIComponent(customerKey)}/entries`, payload).then(r => r.data),
    adminCustomerKhataUpdateEntry: (entryId, payload) =>
        axios.put(`${API}/admin/customer-ledger/entries/${entryId}`, payload).then(r => r.data),
    adminCustomerKhataDeleteEntry: (entryId) =>
        axios.delete(`${API}/admin/customer-ledger/entries/${entryId}`).then(r => r.data),
    adminCustomerKhataRestoreEntry: (entryId) =>
        axios.post(`${API}/admin/customer-ledger/entries/${entryId}/restore`).then(r => r.data),
    adminCustomerKhataSummaryXlsx: (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/customer-ledger-summary/export.xlsx${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    // Phase 51.1 — Customer Khata PDFs
    adminCustomerKhataVoucherPdf: (entryId) =>
        axios.get(`${API}/admin/customer-ledger/entries/${entryId}/voucher.pdf`, { responseType: 'blob' }).then(r => r.data),
    adminCustomerKhataStatementPdf: (customerKey, params = {}) => {
        const qs = new URLSearchParams(params).toString();
        return axios.get(`${API}/admin/customer-ledger/${encodeURIComponent(customerKey)}/statement.pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' }).then(r => r.data);
    },
    // Phase 51.5 — Edit an application installment from Khata Book (syncs back to Applications)
    adminCustomerKhataEditAutoInstallment: (payload) =>
        axios.put(`${API}/admin/customer-ledger/auto-installment`, payload).then(r => r.data),
    // Phase 51.8 — Customer Projects (per-customer cost tracking + auto OUT ledger overlay)
    adminCustomerProjectsList: (customerKey) =>
        axios.get(`${API}/admin/customer-ledger/${encodeURIComponent(customerKey)}/projects`).then(r => r.data),
    adminCustomerProjectAdd: (customerKey, payload) =>
        axios.post(`${API}/admin/customer-ledger/${encodeURIComponent(customerKey)}/projects`, payload).then(r => r.data),
    adminCustomerProjectUpdate: (projectId, payload) =>
        axios.put(`${API}/admin/customer-ledger/projects/${projectId}`, payload).then(r => r.data),
    adminCustomerProjectDelete: (projectId) =>
        axios.delete(`${API}/admin/customer-ledger/projects/${projectId}`).then(r => r.data),
    // Phase 50.3 — voucher numbering series (per FY)
    adminVoucherSeriesList: () =>
        axios.get(`${API}/admin/voucher-series`).then(r => r.data),
    adminVoucherSeriesUpdate: (fyCode, payload) =>
        axios.put(`${API}/admin/voucher-series/${fyCode}`, payload).then(r => r.data),
    adminPaymentReceiptUrl: (appId, pid) => `${API}/admin/applications/${appId}/payments/${pid}/receipt.pdf`,

    // Payment schedule + reminders
    adminGetPaymentSchedule: (appId) => axios.get(`${API}/admin/applications/${appId}/payment-schedule`).then(r => r.data),
    adminReplacePaymentSchedule: (appId, items) => axios.put(`${API}/admin/applications/${appId}/payment-schedule`, items).then(r => r.data),
    adminRunPaymentReminders: () => axios.post(`${API}/admin/payment-reminders/run-now`).then(r => r.data),

    // Quotation format settings (admin-editable PDF template)
    adminGetQuotationSettings: () => axios.get(`${API}/admin/quotation-settings`).then(r => r.data),
    adminUpdateQuotationSettings: (p) => axios.put(`${API}/admin/quotation-settings`, p).then(r => r.data),

    // Multi-Company Branding Profiles
    adminListQuotationProfiles: () => axios.get(`${API}/admin/quotation-profiles`).then(r => r.data),
    adminCreateQuotationProfile: (p) => axios.post(`${API}/admin/quotation-profiles`, p).then(r => r.data),
    adminUpdateQuotationProfile: (id, p) => axios.put(`${API}/admin/quotation-profiles/${id}`, p).then(r => r.data),
    adminSetDefaultQuotationProfile: (id) => axios.put(`${API}/admin/quotation-profiles/${id}/set-default`).then(r => r.data),
    adminDeleteQuotationProfile: (id) => axios.delete(`${API}/admin/quotation-profiles/${id}`).then(r => r.data),

    // Push notifications
    pushRegisterToken: (token, platform = 'android') => axios.post(`${API}/push/register-token`, { token, platform }).then(r => r.data),
    pushUnregisterToken: (token) => axios.post(`${API}/push/unregister-token`, { token }).then(r => r.data),
    adminPushStatus: () => axios.get(`${API}/admin/push/status`).then(r => r.data),
    adminPushBroadcast: (p) => axios.post(`${API}/admin/push/broadcast`, p).then(r => r.data),
    adminPushDevices: (params = {}) => axios.get(`${API}/admin/push/devices`, { params }).then(r => r.data),
    adminPushTest: (p) => axios.post(`${API}/admin/push/test`, p).then(r => r.data),
    adminPushUploadCreds: (service_account) => axios.post(`${API}/admin/push/upload-credentials`, { service_account }).then(r => r.data),
    adminPushDeleteCreds: () => axios.delete(`${API}/admin/push/credentials`).then(r => r.data),

    // Broadcast News / Flash Ticker
    adminBroadcastNewsList: (params = {}) => axios.get(`${API}/admin/broadcast-news`, { params }).then(r => r.data),
    adminBroadcastNewsCreate: (p) => axios.post(`${API}/admin/broadcast-news`, p).then(r => r.data),
    adminBroadcastNewsUpdate: (id, p) => axios.put(`${API}/admin/broadcast-news/${id}`, p).then(r => r.data),
    adminBroadcastNewsToggle: (id, is_active) => axios.put(`${API}/admin/broadcast-news/${id}/toggle`, { is_active }).then(r => r.data),
    adminBroadcastNewsDelete: (id) => axios.delete(`${API}/admin/broadcast-news/${id}`).then(r => r.data),
    broadcastNewsActive: () => axios.get(`${API}/broadcast-news/active`).then(r => r.data),

    // Message templates (admin-editable messaging text)
    adminListMessageTemplates: () => axios.get(`${API}/admin/message-templates`).then(r => r.data),
    adminUpdateMessageTemplate: (p) => axios.put(`${API}/admin/message-templates`, p).then(r => r.data),
    adminResetMessageTemplate: (event) => axios.post(`${API}/admin/message-templates/reset`, { event }).then(r => r.data),
    // Phase 41 — Meta WhatsApp Business template payloads (ready-to-approve).
    adminListMetaTemplates:     () => axios.get(`${API}/admin/message-templates/meta-format`).then(r => r.data),

    // Phase 43 — System Errors feed (server exceptions + client-reported JS errors)
    adminListSystemErrors:    (params = {}) => axios.get(`${API}/admin/system-errors`, { params }).then(r => r.data),
    adminResolveSystemError:  (errId) => axios.post(`${API}/admin/system-errors/${errId}/resolve`).then(r => r.data),
    adminClearResolvedErrors: () => axios.post(`${API}/admin/system-errors/clear-resolved`).then(r => r.data),

    // Phase 45 — Book Visits & Surveys on behalf of customer (Care / Admin)
    adminBookSiteSurvey:  (p) => axios.post(`${API}/admin/site-surveys`,     p).then(r => r.data),
    adminBookSaathiVisit: (p) => axios.post(`${API}/admin/saathi-bookings`,  p).then(r => r.data),

    // Outbox (SMS/WhatsApp sent messages)
    adminOutbox: (params = {}) => axios.get(`${API}/admin/outbox`, { params }).then(r => r.data),
    adminOutboxDetail: (logId) => axios.get(`${API}/admin/outbox/${logId}`).then(r => r.data),

    // WhatsApp Inbox (Nerochat + any provider)
    waThreads:      (params = {}) => axios.get(`${API}/admin/wa/threads`, { params }).then(r => r.data),
    waThreadOpen:   (phone) => axios.get(`${API}/admin/wa/thread/${encodeURIComponent(phone)}`).then(r => r.data),
    waReply:        (body) => axios.post(`${API}/admin/wa/reply`, body).then(r => r.data),
    waMarkRead:     (msg_id) => axios.post(`${API}/admin/wa/mark-read/${encodeURIComponent(msg_id)}`).then(r => r.data),
    waMarkThreadRead: (phone) => axios.post(`${API}/admin/wa/mark-thread-read/${encodeURIComponent(phone)}`).then(r => r.data),

    // Nerochat API request/response inspector
    nerochatApiLogs:      (params = {}) => axios.get(`${API}/admin/nerochat/api-logs`, { params }).then(r => r.data),
    nerochatApiLogDetail: (id) => axios.get(`${API}/admin/nerochat/api-logs/${encodeURIComponent(id)}`).then(r => r.data),
    nerochatAlertConfigGet:    () => axios.get(`${API}/admin/nerochat/alert-config`).then(r => r.data),
    nerochatAlertConfigSave:   (body) => axios.put(`${API}/admin/nerochat/alert-config`, body).then(r => r.data),
    nerochatAlertReset:        () => axios.post(`${API}/admin/nerochat/alert-reset`).then(r => r.data),
    nerochatAlertTestFire:     () => axios.post(`${API}/admin/nerochat/alert-test`).then(r => r.data),

    // Login security (suspicious-login alerts + geo restriction + CSV export)
    loginSecurityGet:    () => axios.get(`${API}/admin/security/login-config`).then(r => r.data),
    loginSecuritySave:   (body) => axios.put(`${API}/admin/security/login-config`, body).then(r => r.data),
    loginEventsCsvUrl:   (params = {}) => {
        const q = new URLSearchParams(params).toString();
        return `${API}/admin/login-events.csv${q ? '?' + q : ''}`;
    },

    // News Flash (public)
    newsFlash: (limit = 10) => axios.get(`${API}/news-flash`, { params: { limit } }).then(r => r.data),

    // User activation (any user - staff or customer)
    adminToggleUserActive: (uid, is_active, reason = '') => axios.put(`${API}/admin/users/${uid}/toggle-active`, { is_active, reason }).then(r => r.data),
    adminBulkToggleActive: (user_ids, is_active, reason = '') => axios.put(`${API}/admin/users/bulk-toggle-active`, { user_ids, is_active, reason }).then(r => r.data),
    adminLoginEvents: (params = {}) => axios.get(`${API}/admin/login-events`, { params }).then(r => r.data),
    myAttendanceHistory: (days = 30) => axios.get(`${API}/attendance/mine/history`, { params: { days } }).then(r => r.data),
    adminRunPunchoutReminder: () => axios.post(`${API}/admin/attendance/run-punchout-reminder`).then(r => r.data),

    // Phase 48 — In-app notifications (bell icon in header)
    listNotifications:    (params = {}) => axios.get(`${API}/notifications`, { params }).then(r => r.data),
    unreadNotifCount:     () => axios.get(`${API}/notifications/unread-count`).then(r => r.data),
    markNotifRead:        (nid) => axios.post(`${API}/notifications/${encodeURIComponent(nid)}/read`).then(r => r.data),
    markAllNotifRead:     (ids) => axios.post(`${API}/notifications/read-all`, ids ? { ids } : {}).then(r => r.data),
    dismissNotif:         (nid) => axios.delete(`${API}/notifications/${encodeURIComponent(nid)}`).then(r => r.data),

    // Phase 49.5 — Meta WhatsApp Cloud API auto-submit
    getMetaWaConfig:       () => axios.get(`${API}/admin/meta-wa-config`).then(r => r.data),
    saveMetaWaConfig:      (payload) => axios.put(`${API}/admin/meta-wa-config`, payload).then(r => r.data),
    submitTemplateToMeta:  (event, lang = 'en') => axios.post(
        `${API}/admin/message-templates/${encodeURIComponent(event)}/submit-to-meta`,
        { lang },
    ).then(r => r.data),
    bulkSubmitToMeta:      (lang = 'en', only_missing = false) => axios.post(
        `${API}/admin/message-templates/bulk-submit-to-meta`, { lang, only_missing },
    ).then(r => r.data),
    getTemplateMetaStatus: (event) => axios.get(`${API}/admin/message-templates/${encodeURIComponent(event)}/meta-status`).then(r => r.data),
    getAllMetaStatuses:    () => axios.get(`${API}/admin/message-templates/meta-status-all`).then(r => r.data),
    metaStatusSyncNow:     () => axios.post(`${API}/admin/message-templates/meta-status-sync-now`).then(r => r.data),
    listMetaSubmissions:   () => axios.get(`${API}/admin/message-templates/meta-submissions`).then(r => r.data),

    // Phase 49 — Lead Labels + Stages + Activity Timeline
    listLeadLabels:     () => axios.get(`${API}/admin/lead-labels`).then(r => r.data),
    createLeadLabel:    (payload) => axios.post(`${API}/admin/lead-labels`, payload).then(r => r.data),
    updateLeadLabel:    (id, payload) => axios.put(`${API}/admin/lead-labels/${encodeURIComponent(id)}`, payload).then(r => r.data),
    deleteLeadLabel:    (id) => axios.delete(`${API}/admin/lead-labels/${encodeURIComponent(id)}`).then(r => r.data),
    listLeadStages:     () => axios.get(`${API}/admin/lead-stages`).then(r => r.data),
    createLeadStage:    (payload) => axios.post(`${API}/admin/lead-stages`, payload).then(r => r.data),
    updateLeadStage:    (id, payload) => axios.put(`${API}/admin/lead-stages/${encodeURIComponent(id)}`, payload).then(r => r.data),
    deleteLeadStage:    (id) => axios.delete(`${API}/admin/lead-stages/${encodeURIComponent(id)}`).then(r => r.data),
    setLeadLabels:      (lid, label_ids) => axios.put(`${API}/admin/leads/${encodeURIComponent(lid)}/labels`, { label_ids }).then(r => r.data),
    setLeadStage:       (lid, stage_id)  => axios.put(`${API}/admin/leads/${encodeURIComponent(lid)}/stage`, { stage_id }).then(r => r.data),
    addLeadActivity:    (lid, payload)   => axios.post(`${API}/admin/leads/${encodeURIComponent(lid)}/activities`, payload).then(r => r.data),
    listLeadActivities: (lid) => axios.get(`${API}/admin/leads/${encodeURIComponent(lid)}/activities`).then(r => r.data),
};
